/**
 * Run Clock Bluetooth setup (#374): the pure parts of the protocol the clock
 * speaks over its provisioning GATT service. The UUIDs and JSON shapes mirror
 * pbranks-cyd/src/net/ble_prov.h — change both together.
 */

export const RUN_CLOCK_SERVICE = "7b6a5c40-2f1e-4d8b-9a3c-0e5f6a7b8c9d";
export const RUN_CLOCK_CHAR_INFO = "7b6a5c41-2f1e-4d8b-9a3c-0e5f6a7b8c9d";
export const RUN_CLOCK_CHAR_NETS = "7b6a5c42-2f1e-4d8b-9a3c-0e5f6a7b8c9d";
export const RUN_CLOCK_CHAR_CREDS = "7b6a5c43-2f1e-4d8b-9a3c-0e5f6a7b8c9d";
export const RUN_CLOCK_CHAR_STATUS = "7b6a5c44-2f1e-4d8b-9a3c-0e5f6a7b8c9d";
/** Advertised name prefix; the suffix is the last four hex digits of the clock's MAC. */
export const RUN_CLOCK_NAME_PREFIX = "PBRanks-";

export type RunClockStatusKind =
  | "idle"
  | "connecting"
  | "wifi_failed"
  | "online"
  | "pairing"
  | "paired";

export type RunClockStatus = {
  st: RunClockStatusKind;
  /** The 6-digit pairing code, present from `pairing` onwards. */
  code?: string;
  ip?: string;
};

export type RunClockInfo = {
  fw: number;
  mac: string;
  /** The clock's own WiFi AP name, for the browser fallback hint. */
  ap: string;
};

const STATUS_KINDS: RunClockStatusKind[] = ["idle", "connecting", "wifi_failed", "online", "pairing", "paired"];

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Decode a characteristic value (UTF-8 JSON) into a status; null if malformed. */
export function parseStatus(text: string): RunClockStatus | null {
  const raw = parseJson(text);
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const st = o.st;
  if (typeof st !== "string" || !STATUS_KINDS.includes(st as RunClockStatusKind)) return null;
  const out: RunClockStatus = { st: st as RunClockStatusKind };
  if (typeof o.code === "string" && /^\d{6}$/.test(o.code)) out.code = o.code;
  if (typeof o.ip === "string" && o.ip) out.ip = o.ip;
  return out;
}

/** The SSIDs the clock can see, in the order it reported them (strongest first). */
export function parseNets(text: string): string[] {
  const raw = parseJson(text);
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== "string" || !v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

export function parseInfo(text: string): RunClockInfo | null {
  const raw = parseJson(text);
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    fw: typeof o.fw === "number" ? o.fw : 0,
    mac: typeof o.mac === "string" ? o.mac : "",
    ap: typeof o.ap === "string" ? o.ap : "",
  };
}

/**
 * The credentials payload the clock expects on the `creds` characteristic.
 * `deviceId` is the pre-registered device from POST /tv/provision: with it the
 * clock skips the on-screen code and polls straight into a paired state.
 */
export function credentialsPayload(ssid: string, password: string, deviceId?: string): string {
  return JSON.stringify(deviceId ? { ssid, pass: password, deviceId } : { ssid, pass: password });
}

/** "PBRanks-1A2B" -> "Run Clock 1A2B"; anything else is shown as-is. */
export function friendlyClockName(advertisedName: string | undefined): string {
  if (!advertisedName) return "Run Clock";
  return advertisedName.startsWith(RUN_CLOCK_NAME_PREFIX)
    ? `Run Clock ${advertisedName.slice(RUN_CLOCK_NAME_PREFIX.length)}`
    : advertisedName;
}
